import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { FeatureFlagService } from '@cudirect/shared-ui-library';
import { RequestHeaderConst } from '@workflow/ecp-shared/constant/information.const';
import { DataLoadURL } from '@workflow/ecp-shared/model/dataload-url';
import { ApiService } from '@workflow/ecp-shared/service/api/api.service';
import { ShareResponse, Product } from '../../types/accounts-and-services.type';
import { BranchList } from '../../types/branchList.type';
import { Disclosure } from '../../types/disclosure.type';
import { AdditionalCustomField, CustomFields } from '../../types/dynamic-custom-fields.type';
import { LenderEligibilityQuestionnaire } from '../../types/dynamic-eligibility-questions';
import { PickListTypes, PickList, PickListItem } from '../../types/picklist';
import {
  PreciseIDQuestionResponse,
  PreciseIDQuestionAnswerResponse,
  PreciseIdAnswerSubmit,
  PreciseIdCorrelationData,
  PreciseIdOOWAnswersPayload,
  OOWQuestionEntity,
  PreciseIdOOWAnswersEntity
} from '../../types/preciseIdQuestionResponse';
import { ApplicationApiService } from '../application-api/application-api.service';
import { ApiResult } from '../application-api/application-api.types';
import { Loan_api } from '../payment/payment.const';
import { StatefulService } from '@app/service/stateful.service';

/** L360 Service */
@Injectable({
  providedIn: 'root'
})
export class L360Service {
  /** Consumer portal API Host Name */
  private readonly hostName = 'consumerPortalApi';
  // protected readonly httpHeaders = this.httpOptions();
  /** Manual trigger for Precise ID questions updates */
  public submissionPreciseIdAnswers: Subject<boolean> = new Subject<boolean>();
  /** Cached Eligibility */
  public cachedEligibility: LenderEligibilityQuestionnaire = null;
  public cachedCustomfields: CustomFields[] = [];
  public pickListData: PickListTypes;
  public LoanPurposePickList: PickList[] = [];
  public identificationPickList: PickList[] = [];
  public cachedAccountAndService: ShareResponse;
  public picklistLoading: { [key: string]: boolean } = {};
  private disclosures: Disclosure[] = [];
  private isDisclosureLoading = false;
  private isDisclosureLoaded = false;
  public disclosureSub: Subject<Disclosure[]> = new Subject<Disclosure[]>();

  private readonly statefulService = inject(StatefulService);

  constructor(
    protected readonly httpClient: HttpClient,
    protected readonly apiService: ApiService,
    public readonly applicationApiService: ApplicationApiService,
    public readonly router: Router,
    public readonly featureFlagService: FeatureFlagService
  ) {}

  /**
   * Set Picklist config data
   *   * @param data - Picklist Data
   */
  public set pickListConfigData(data: PickListTypes) {
    this.pickListData = data;
  }

  /**
   * Set Loan Purpose Picklist Data
   *   * @param data - Loan Purpose Data
   */
  public set LoanPurposePickListData(data: PickListTypes) {
    this.LoanPurposePickList = this.LoanPurposePickList
      ? this.mergePicklist(data?.picklistTypes, this.LoanPurposePickList)
      : data?.picklistTypes;
  }

  public get getIsDisclosureLoaded(): boolean {
    return this.isDisclosureLoaded;
  }
  /**
   * Merge Picklist
   * @param sourceitems - Source picklist
   * @param targetitems - Target picklist
   * @returns an array of Merged and Updated PickListTypes
   */
  private mergePicklist(sourceitems: PickList[], targetitems: PickList[]): PickList[] {
    // Use Sets to remove duplicates from source and target arrays based on 'loanPurposeSubkey'
    const sourceSet = new Set(sourceitems.map((item) => item.loanPurposeSubkey));
    // Create a new array by concatenating source and target arrays, filtering duplicates based on 'loanPurposeSubkey'
    const mergedArray = [...sourceitems, ...targetitems?.filter((item) => !sourceSet.has(item.loanPurposeSubkey))];
    return mergedArray;
  }

  /**
   * Get Picklist Types
   * @param keyType - key to get picklist types
   * @param loanPurposeSubkey - key to get Loan Purpose picklist types
   * @returns an array of PickListTypes
   */
  public getPickListTypes(keyType: string, loanPurposeSubkey?: string): PickList[] {
    if ((!this.pickListData && !loanPurposeSubkey) || (loanPurposeSubkey && !this.LoanPurposePickList)) {
      return [];
    }

    if (keyType?.toLowerCase().includes('identification')) {
      return this.identificationPickList;
    }

    // use Set<string> for keys instead of an array to take advantage of faster lookups with Set.has compared to Array.includes.
    const keys = keyType ? new Set(keyType.split(',').map((t) => t?.trim()?.toLowerCase())) : new Set<string>();
    const subkey = loanPurposeSubkey?.toLowerCase();

    let picklistTypes = [];
    if (loanPurposeSubkey) {
      picklistTypes = this.LoanPurposePickList.filter(
        (f) => (!keys.size || keys.has(f?.value?.toLowerCase())) && (!subkey || f?.loanPurposeSubkey?.toLowerCase() === subkey)
      );
    } else {
      picklistTypes = this.pickListData.picklistTypes.filter((f) => !keys.size || keys.has(f?.value?.toLowerCase()));
    }
    return picklistTypes;
  }

  public getMemberships(): Observable<object> {
    const urlObj = {
      hostName: this.hostName,
      method: 'GET',
      url: 'member/oao/integration/memberships'
    } as DataLoadURL;
    return this.apiService.request<object>({ urlObj });
  }

  public getProductIdentificationTypes(loanGroupId: string, loanProductId: string, cb: (response: PickList[]) => void): void {
    if (this.identificationPickList?.length > 0 && cb) {
      cb(this.identificationPickList);
    }
    const urlObj = {
      hostName: this.hostName,
      method: 'GET',
      url: 'admintoolintegration/identificationtypes/{groupId}/{productId}'
    } as DataLoadURL;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.apiService.request<any>({ urlObj, urlIds: { groupId: loanGroupId, productId: loanProductId } }).subscribe((res) => {
      if (cb && res) {
        this.identificationPickList = res?.data?.picklistTypes;
        cb(res.data.picklistTypes);
      }
    });
  }

  public getIdentificationTypes(Id: string): Observable<object> {
    const urlObj = {
      hostName: this.hostName,
      method: 'GET',
      url: 'admintoolintegration/identificationtypes/{membershipId}'
    } as DataLoadURL;
    return this.apiService.request<object>({ urlObj, urlIds: { membershipId: Id } });
  }

  public getEmploymentStatus(Id: string): Observable<object> {
    const urlObj = {
      hostName: this.hostName,
      method: 'GET',
      url: 'member/oao/integration/employmentstatus/{membershipId}'
    } as DataLoadURL;
    return this.apiService.request<object>({ urlObj, urlIds: { membershipId: Id } });
  }

  public getIncomeFrequency(): Observable<object> {
    const urlObj = {
      hostName: this.hostName,
      method: 'GET',
      url: 'admintoolintegration/frequency'
    } as DataLoadURL;
    return this.apiService.request<object>({ urlObj });
  }

  /** Clear the disclosure */
  public clearDisclosure(): void {
    this.isDisclosureLoaded = false;
    this.isDisclosureLoading = false;
    this.disclosures = [];
  }

  /** Get disclsoures*/
  public getDisclsoures(): void {
    this.statefulService.wrap('mem:show-card-spinner').value = true;
    if (this.isDisclosureLoading || this.isDisclosureLoaded) {
      this.disclosureSub.next(this.disclosures ?? ([] as Disclosure[]));
      this.statefulService.wrap('mem:show-card-spinner').value = false;
      return;
    }

    this.isDisclosureLoading = true;
    this.isDisclosureLoaded = false;
    this.disclosures = [];
    this.getFinalReviewDisclsoure().subscribe(
      (response: ApiResult<Disclosure[]>) => {
        if (response.isSuccess && response.data) {
          this.disclosures = response.data;
        }
        this.isDisclosureLoading = false;
        this.isDisclosureLoaded = true;
        this.statefulService.wrap('mem:show-card-spinner').value = false;
        this.disclosureSub.next(this.disclosures);
      },
      () => {
        this.isDisclosureLoading = false;
        this.statefulService.wrap('mem:show-card-spinner').value = false;
      }
    );
  }

  public getFinalReviewDisclsoure(): Observable<ApiResult<Disclosure[]>> {
    const urlObj = {
      defaultHeaders: {
        [RequestHeaderConst.authorization]: this.statefulService.wrap('ss:authorization').valueString,
        [RequestHeaderConst.dxSessionId]: this.statefulService.wrap('ss:authorization').valueString
      },
      hostName: this.hostName,
      method: 'GET',
      url: 'member/oao/integration/finalreviewdisclosure'
    } as DataLoadURL;
    return this.apiService.request<ApiResult<Disclosure[]>>({ urlObj });
  }

  public getBranchList(): Observable<ApiResult<BranchList[]>> {
    const urlObj = {
      defaultHeaders: {
        [RequestHeaderConst.authorization]: this.statefulService.wrap('ss:auth-ola').valueString,
        [RequestHeaderConst.dxSessionId]: this.statefulService.wrap('ss:auth-ola').valueString
      },
      hostName: this.hostName,
      method: 'GET',
      url: 'member/oao/integration/GetBranches'
    } as DataLoadURL;
    return this.apiService.request<ApiResult<BranchList[]>>({ urlObj });
  }

  public setOAOLenderSettings(redirectPath?: string): void {
    const urlObj = {
      hostName: this.hostName,
      method: 'GET',
      url: 'member/oao/integration/OAOLenderSettings'
    } as DataLoadURL;

    this.apiService.request<ApiResult<boolean>>({ urlObj }).subscribe(
      (data) => {
        if (data?.isSuccess) {
          this.statefulService.wrap('api:oaoMemberInformation/oaoLenderSettings/isPIDQuestionsAvailable').value = data.data;
          if (redirectPath) {
            this.router.navigate([redirectPath]);
          }
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      (error) => {
        if (redirectPath) {
          this.router.navigate([redirectPath]);
        }
      }
    );
  }

  public getPreciseIDQuestions(applicationId: string, applicantId: string): Observable<ApiResult<PreciseIDQuestionResponse>> {
    const urlObj = {
      defaultHeaders: {
        [RequestHeaderConst.authorization]: this.statefulService.wrap('ss:authorization').valueString,
        [RequestHeaderConst.dxSessionId]: this.statefulService.wrap('ss:authorization').valueString
      },
      hostName: this.hostName,
      method: 'POST',
      url: `member/oao/integration/GetPreciseIdQuestions`
    } as DataLoadURL;
    return this.apiService.request<ApiResult<PreciseIDQuestionResponse>>({
      urlObj,
      body: { applicationId, applicantId }
    });
  }
  public savePreciseIDAnswers(): Observable<ApiResult<PreciseIDQuestionAnswerResponse>> {
    const urlObj = {
      defaultHeaders: {
        [RequestHeaderConst.authorization]: this.statefulService.wrap('ss:authorization').valueString,
        [RequestHeaderConst.dxSessionId]: this.statefulService.wrap('ss:authorization').valueString
      },
      hostName: this.hostName,
      method: 'POST',
      url: 'member/oao/integration/SavePreciseIdAnswers'
    } as DataLoadURL;
    const preciseIdOOWAnswersPayload: PreciseIdAnswerSubmit = this.preparePreciseIdAnswersModel();
    return this.apiService.request<ApiResult<PreciseIDQuestionAnswerResponse>>({ urlObj, body: preciseIdOOWAnswersPayload });
  }

  public getDynamicEligibilityQuestions(): Observable<ApiResult<LenderEligibilityQuestionnaire>> {
    const urlObj = {
      hostName: this.hostName,
      method: 'GET',
      url: 'member/oao/integration/dynamicEligibility'
    } as DataLoadURL;
    return this.apiService.request<ApiResult<LenderEligibilityQuestionnaire>>({ urlObj });
  }

  public getLegalDocuments(value: string, mimeType?: string): Observable<Blob> {
    const temp_mimeType = !mimeType ? RequestHeaderConst.contentType : mimeType;
    return this.apiService.postImage({
      body: { value, mimeType: temp_mimeType },
      urlObj: Loan_api.LegalDocument
    });
  }

  public getOAODynamicCustomFields({ membershipID }: { membershipID: string }): Observable<ApiResult<AdditionalCustomField>> {
    const urlObj = {
      defaultHeaders: {
        [RequestHeaderConst.contentTypeName]: 'application/json',
        [RequestHeaderConst.authorization]: this.statefulService.wrap('ss:authorization').valueString,
        [RequestHeaderConst.dxSessionId]: this.statefulService.wrap('ss:authorization').valueString
      },
      hostName: this.hostName,
      method: 'POST',
      url: 'member/oao/integration/GetCustomFields'
    } as DataLoadURL;
    return this.apiService.request<ApiResult<AdditionalCustomField>>({ urlObj, body: JSON.stringify(membershipID) });
  }

  public getOLADynamicCustomFields({
    loanGroupName,
    loanProductName
  }: {
    loanGroupName: string;
    loanProductName: string;
  }): Observable<ApiResult<AdditionalCustomField>> {
    const urlObj = {
      defaultHeaders: {
        [RequestHeaderConst.contentTypeName]: 'application/json',
        [RequestHeaderConst.authorization]: this.statefulService.wrap('ss:auth-ola').valueString,
        [RequestHeaderConst.dxSessionId]: this.statefulService.wrap('ss:auth-ola').valueString
      },
      hostName: this.hostName,
      method: 'POST',
      url: 'consumer/ola/dynamic/GetCustomFields'
    } as DataLoadURL;
    return this.apiService.request<ApiResult<AdditionalCustomField>>({ urlObj, body: { loanGroupName, loanProductName } });
  }

  /** TODO: Consumers of this endpoint should have additional metadata added to their custom events -
   * **See ola runautomateddecision and oao submit implementation**
   */
  public GetEligibileProductsFromL360(): Observable<ApiResult<ShareResponse>> {
    const urlObj = {
      hostName: this.hostName,
      defaultHeaders: {
        [RequestHeaderConst.authorization]: this.statefulService.wrap('ss:authorization').valueString,
        [RequestHeaderConst.dxSessionId]: this.statefulService.wrap('ss:authorization').valueString
      },
      method: 'POST',
      url: 'consumer/dynamic/runAutomatedDecision'
    } as DataLoadURL;

    return this.apiService.request<ApiResult<ShareResponse>>({ urlObj });
  }

  public validateDraftApp(): void {
    const urlObj = {
      hostName: this.hostName,
      defaultHeaders: {
        [RequestHeaderConst.authorization]: this.statefulService.wrap('ss:authorization').valueString,
        [RequestHeaderConst.dxSessionId]: this.statefulService.wrap('ss:authorization').valueString
      },
      method: 'GET',
      url: 'consumer/dynamic/validateDraftApplication'
    } as DataLoadURL;

    this.apiService.request<ApiResult<boolean>>({ urlObj }).subscribe(
      (data) => {
        if (data && data.isSuccess) {
          // if (redirectPath) {
          // this.router.navigate([redirectPath]);
          // }
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      (error) => {
        // this.router.navigate([redirectPath]);
      }
    );
  }

  public validateOLADraftApp(): void {
    const urlObj = {
      defaultHeaders: {
        [RequestHeaderConst.authorization]: this.statefulService.wrap('ss:auth-ola').valueString,
        [RequestHeaderConst.dxSessionId]: this.statefulService.wrap('ss:auth-ola').valueString
      },
      hostName: this.hostName,
      method: 'GET',
      url: 'consumer/ola/dynamic/validateDraftApplication'
    } as DataLoadURL;

    this.apiService.request<ApiResult<boolean>>({ urlObj }).subscribe(
      (data) => {
        if (data && data.isSuccess) {
          // if (redirectPath) {
          // this.router.navigate([redirectPath]);
          // }
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      (error) => {
        // this.router.navigate([redirectPath]);
      }
    );
  }

  public UpdateBundleIndex(products: Product[]): Observable<object> {
    const urlObj = {
      hostName: this.hostName,
      method: 'POST',
      url: 'member/oao/integration/UpdateBundleIndex',
      defaultHeaders: {
        [RequestHeaderConst.authorization]: this.statefulService.wrap('ss:authorization').valueString,
        [RequestHeaderConst.dxSessionId]: this.statefulService.wrap('ss:authorization').valueString
      }
    } as DataLoadURL;

    return this.apiService.request<Product[]>({ urlObj, body: products });
  }

  private preparePreciseIdAnswersModel(): PreciseIdAnswerSubmit {
    const isEmptyPID = this.statefulService.wrap('ss:oaoMemberInformation/isEmptyPIDReport').value;
    const preciseIDQuestionResponse: PreciseIDQuestionResponse = JSON.parse(
      // eslint-disable-next-line prettier/prettier
      this.statefulService.wrap('ss:oaoMemberInformation/identityVerificationQuestions').valueStringOnly
    );

    if (!isEmptyPID) {
      const preciseIdOOWAnswersPayload: PreciseIdAnswerSubmit = {
        isEmptyPIDReport: false,
        sessionId: preciseIDQuestionResponse.sessionId,
        pidType: preciseIDQuestionResponse.pidType,
        OOWApplicantAnswers: {
          preciseIdCorrelationData: {
            correlationId: preciseIDQuestionResponse.correlationId,
            correlationRequestId: preciseIDQuestionResponse.correlationRequestId,
            correlationSourcePlatform: preciseIDQuestionResponse.correlationSourcePlatform,
            lenderCode: preciseIDQuestionResponse.lenderCode
          } as PreciseIdCorrelationData,
          preciseIdOOWAnswers: this.prepareAnsersArray(preciseIDQuestionResponse.oowQuestion)
        } as PreciseIdOOWAnswersPayload
      };
      return preciseIdOOWAnswersPayload;
    } else {
      const preciseIdOOWAnswersPayload: PreciseIdAnswerSubmit = {
        isEmptyPIDReport: true,
        sessionId: '',
        pidType: preciseIDQuestionResponse.pidType,
        OOWApplicantAnswers: {
          preciseIdCorrelationData: {
            correlationId: preciseIDQuestionResponse.correlationId,
            correlationRequestId: '',
            correlationSourcePlatform: '',
            lenderCode: ''
          } as PreciseIdCorrelationData,
          preciseIdOOWAnswers: []
        } as PreciseIdOOWAnswersPayload
      };
      return preciseIdOOWAnswersPayload;
    }
  }

  private prepareAnsersArray(oowQuestion: OOWQuestionEntity[]): PreciseIdOOWAnswersEntity[] {
    const answers: PreciseIdOOWAnswersEntity[] = [];
    oowQuestion.forEach((question) => {
      answers.push({
        questionChoiceId: question.answer,
        relativeOrder: question.relativeOrder
      });
    });
    return answers;
  }

  public getPickListItems(keyType: string, loanPurposeSubkey?: string): Observable<ApiResult<PickListTypes>> {
    const urlObj = {
      defaultHeaders: { 'content-type': 'application/json' },
      hostName: this.hostName,
      method: 'POST',
      url: `integration/l360/PicklistItems`
    } as DataLoadURL;
    const body = {
      PicklistKeys: keyType,
      LoanPurposeSubkey: loanPurposeSubkey
    };

    return this.apiService.request<ApiResult<PickListTypes>>({ urlObj, body });
  }

  public getSpecificBusinessClassification(id: number): Observable<ApiResult<PickListItem[]>> {
    const urlObj = {
      hostName: this.hostName,
      method: 'GET',
      url: `member/oao/integration/GetSpecificBusinessClassification/${id}`
    } as DataLoadURL;
    return this.apiService.request<ApiResult<PickListItem[]>>({ urlObj });
  }

  public getSpecificIndustryClassification(id: number): Observable<ApiResult<PickListItem[]>> {
    const urlObj = {
      hostName: this.hostName,
      method: 'GET',
      url: `member/oao/integration/GetSpecificIndustryClassification/${id}`
    } as DataLoadURL;
    return this.apiService.request<ApiResult<PickListItem[]>>({ urlObj });
  }

  public getVehicleMake(id: number): Observable<ApiResult<PickListItem[]>> {
    const urlObj = {
      hostName: this.hostName,
      method: 'GET',
      url: `integration/l360/GetVehicleMake/${id}`
    } as DataLoadURL;
    return this.apiService.request<ApiResult<PickListItem[]>>({ urlObj });
  }
}
